import "../../../assets/scss/machine-card-style.scss";
import { useTranslation } from "react-i18next";
import { ProviderMachineCardInterface } from "../../utility/interface/provider-machine-card";

import {
  AppstoreOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  DatabaseOutlined,
  DesktopOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { DatePicker, Modal, Table, TableProps, TimePicker, message, Radio, Input, Select, Checkbox } from "antd";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { installHost, startHost, stopHost, createHostSchedule } from "../../server/Hosts";

dayjs.extend(utc);
dayjs.extend(timezone);

enum HostStatus {
  NotInstalled = 0,
  Inactive = 1,
  Active = 2,
}

export const ProviderMachineCard = (props: ProviderMachineCardInterface) => {
  const { t } = useTranslation();
  const {
    host_id,
    status,
    node_name,
    product_name,
    gpu_type,
    gpu_total,
    gpu_in_use,
    core_utilization,
    memory_utilization,
    disk_utilization,
    gpu_live_report,
    onScheduleCreated,
  } = props;
  const [openGPUStatusModal, setOpenGPUStatusModal] = useState<boolean>(false);
  const [openScheduleModal, setOpenScheduleModal] = useState<boolean>(false);
  const [scheduleType, setScheduleType] = useState<"once" | "cron">("once");
  const [scheduleAction, setScheduleAction] = useState<"online" | "offline">("online");
  const [scheduleDateTime, setScheduleDateTime] = useState<Dayjs | null>(null);
  const [cronExpression, setCronExpression] = useState<string>("");
  const [forceOffline, setForceOffline] = useState<boolean>(false);

  const handleScheduleSubmit = async () => {
    try {
      if (scheduleType === "once") {
        if (!scheduleDateTime) return;

        const executeAt = scheduleDateTime.utc().format(); 
        
        const payload = {
          action: scheduleAction,
          type: "once",
          execute_at: executeAt,
          force_offline: forceOffline,
        };
        await createHostSchedule(host_id!, payload);
      } else if (scheduleType === "cron") {
        if (!cronExpression) return;
        
        // Grab the user's local timezone (e.g. "Asia/Taipei")
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezoneAwareCron = `CRON_TZ=${userTimezone} ${cronExpression}`;

        const payload = {
          action: scheduleAction,
          type: "cron",
          cron_expression: timezoneAwareCron,
          force_offline: forceOffline,
        };
        await createHostSchedule(host_id!, payload);
      }

      message.success(t("Schedule created successfully"));
      setOpenScheduleModal(false);
      
      // Reset states
      setScheduleDateTime(null);
      setCronExpression("");
      setScheduleAction("online");
      setForceOffline(false);

      if (onScheduleCreated) {
        onScheduleCreated();
      }
    } catch (err: any) {
      console.error(err);
      message.error(err?.response?.data?.msg || t("Failed to create schedule"));
    }
  };

  interface GPULiveReportDataType {
    id?: number;
    in_use?: boolean;
    temperature?: number;
    power_usage?: number;
    memory_used?: number;
    memory_total?: number;
    core_utilization?: number;
  }
  const mwToW = (mw: number) => {
    let num = mw / 1000;
    return num.toFixed(2);
  };

  const percentage = (percentage: number) => {
    let number = Number(percentage);
    return number.toFixed(3);
  };

  const gpuLiveReportColumns: TableProps<GPULiveReportDataType>["columns"] = [
    {
      title: t("In Use"),
      dataIndex: "in_use",
      key: "in_use",
      render: (value) => (
        <>
          {value == true ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          ) : (
            <CloseCircleTwoTone twoToneColor="#0066ff" />
          )}
        </>
      ),
    },
    { title: t("ID"), dataIndex: "id", key: "id" },
    {
      title: t("Temperature"),
      dataIndex: "temperature",
      key: "temperature",
      render: (value) => <>{value} °C</>,
    },
    {
      title: t("Power Usage"),
      dataIndex: "power_usage",
      key: "power_usage",
      render: (value) => <>{mwToW(value)} W</>,
    },
    {
      title: t("Memory Used"),
      dataIndex: "memory_used",
      key: "memory_used",
      render: (value) => <>{value} MiB</>,
    },
    {
      title: t("Memory Total"),
      dataIndex: "memory_total",
      key: "memory_total",
      render: (value) => <>{value} MiB</>,
    },
    {
      title: t("Core Utilization"),
      dataIndex: "core_utilization",
      key: "core_utilization",

      render: (value) => <>{value} %</>,
    },
  ];

  const returnHealthyStatus = (status: number) => {
    if (status === 0 || status === 2) {
      return "not-healthy";
    }

    if (status === 1) {
      return "healthy";
    }
  };

  const toggleActiveHost = async (host_id: string) => {
    try {
      let response;

      switch (status) {
        case HostStatus.NotInstalled: // 0 -> 1: install
          response = await installHost(host_id);
          break;
        case HostStatus.Inactive: // 1 -> 2: start
          response = await startHost(host_id);
          break;
        case HostStatus.Active: // 2 -> 1: stop
          response = await stopHost(host_id);
          break;
        default:
          return;
      }

      const data = response.data;
      if (data.code == 0) {
        window.location.reload();
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      <div className="machine-card">
        <ul className="machine-card-details">
          <div className="machine-header">
            <div className="machine-title">
              <span
                className={`active-dot ${
                  status === HostStatus.Active ? "active" : "inactive"
                }`}
              ></span>
              <h3>{node_name}</h3>
            </div>
            <span className="machine-subtext">
              <DesktopOutlined /> ID: {host_id} •{" "}
              {gpu_type == "nv" ? "Nvidia" : gpu_type}
            </span>
            <span className="machine-subtext">
              <AppstoreOutlined /> {product_name}
            </span>
          </div>

          <div className="machine-gpu-summary">
            <div className="gpu-metric">
              <DatabaseOutlined />
              <span className="value">
                {gpu_in_use}/{gpu_total}
              </span>
              <span className="label">{t("GPU In Use")}</span>
            </div>
          </div>

          <div className="machine-stats">
            <div className="stat">
              <span>{t("CPU")}</span>
              <div className="bar">
                <div
                  className="fill"
                  style={{ width: `${core_utilization}%` }}
                ></div>
              </div>
              <span className="percent">{percentage(core_utilization)}%</span>
            </div>

            <div className="stat">
              <span>{t("Memory")}</span>
              <div className="bar">
                <div
                  className="fill"
                  style={{ width: `${memory_utilization}%` }}
                ></div>
              </div>
              <span className="percent">
                {percentage(memory_utilization)}%
              </span>
            </div>

            <div className="stat">
              <span>{t("Disk")}</span>
              <div className="bar">
                <div
                  className="fill"
                  style={{ width: `${disk_utilization}%` }}
                ></div>
              </div>
              <span className="percent">{percentage(disk_utilization)}%</span>
            </div>
          </div>
        </ul>

        <div className="action-section">
          <button
            className="main machine-action-btn"
            onClick={() => setOpenGPUStatusModal(true)}
          >
            {t("Details")}
          </button>
          <button
            className="machine-action-btn"
            onClick={() => setOpenScheduleModal(true)}
          >
            {t("Schedule")}
          </button>
          <button
            className="machine-action-btn"
            onClick={() => toggleActiveHost(host_id!)}
          >
            {status === HostStatus.Active
              ? t("Turn Off Machine")
              : status === HostStatus.Inactive
                ? t("Turn On Machine")
                : t("Install Machine")}
          </button>
        </div>
      </div>
      <Modal
        centered
        width={"80%"}
        open={openGPUStatusModal}
        onCancel={() => setOpenGPUStatusModal(false)}
        footer={null}
      >
        <div style={{ padding: "1.75em .25em" }}>
          <Table
            columns={gpuLiveReportColumns}
            dataSource={gpu_live_report}
            showHeader={gpu_live_report && gpu_live_report.length !== 0}
            tableLayout="fixed"
            pagination={false}
          />
        </div>
      </Modal>
      <Modal
        centered
        title={t("Schedule Machine")}
        open={openScheduleModal}
        onCancel={() => setOpenScheduleModal(false)}
        onOk={handleScheduleSubmit}
        okText={t("Confirm")}
        cancelText={t("Cancel")}
        okButtonProps={{
          disabled: scheduleType === "once" 
            ? !scheduleDateTime 
            : !cronExpression,
        }}
      >
        <div
          style={{
            padding: "1em 0",
            display: "flex",
            flexDirection: "column",
            gap: "1.25em",
          }}
        >
          <div>
            <Radio.Group 
              value={scheduleType} 
              onChange={(e) => setScheduleType(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="once">{t("Once")}</Radio.Button>
              <Radio.Button value="cron">{t("Cron")}</Radio.Button>
            </Radio.Group>
          </div>

          <div>
            <p style={{ marginBottom: "0.5em", fontWeight: 500 }}>
              {t("Action")}
            </p>
            <Select
              style={{ width: "100%" }}
              value={scheduleAction}
              onChange={(val) => setScheduleAction(val)}
              options={[
                { value: 'online', label: t('Online') },
                { value: 'offline', label: t('Offline') },
              ]}
            />
          </div>

          {scheduleType === "once" && (
            <div>
              <p style={{ marginBottom: "0.5em", fontWeight: 500 }}>
                {t("Date & Time")}
              </p>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%" }}
                value={scheduleDateTime}
                onChange={(datetime) => setScheduleDateTime(datetime)}
                disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
                showNow={false}
                needConfirm={false}
              />
            </div>
          )}

          {scheduleType === "cron" && (
            <div>
              <p style={{ marginBottom: "0.5em", fontWeight: 500 }}>
                {t("Cron Expression (6 fields with seconds)")}
              </p>
              <Input 
                placeholder="e.g. 0 0 2 * * *" 
                value={cronExpression}
                onChange={(e) => setCronExpression(e.target.value)}
              />
            </div>
          )}

          {scheduleAction === "offline" && (
            <div>
              <Checkbox 
                checked={forceOffline}
                onChange={(e) => setForceOffline(e.target.checked)}
              >
                {t("Force Evict Instances")}
              </Checkbox>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
